import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { CHAT_MESSAGE_MAX } from "@probetalab/contracts";
import { badgeText, bubbleLabel, messageTime, useChat } from "../lib/chat.js";
import { Icon } from "./icons.js";

/**
 * The floating «Chat entre sucursales» (ADR 0028). Placement never covers a primary action:
 *
 * - Wide and tablet screens: the bubble sits in the sidebar, in flow, right above the user block
 *   (bottom-left), so no page content, sticky action bar or toast can be under it.
 * - Phones (≤ 720 px, the sidebar is a drawer): a floating bubble bottom-left, lifted above any
 *   sticky action bar on the page (`.action-bar`, `.analyte-order-bar`) by measuring it.
 *
 * The panel is a popover next to the sidebar on a desktop, a full-height drawer on the tablet and a
 * bottom sheet on the phone. Esc or the bubble closes it; focus stays inside while open.
 */

export function ChatButton({ variant }: { variant: "rail" | "float" }) {
  const chat = useChat();
  const lift = useActionBarLift(variant === "float");
  if (!chat.enabled) return null;
  const badge = badgeText(chat.unread);
  return (
    <button
      type="button"
      className={`chat-fab ${variant} ${chat.pulse && !chat.open ? "pulse" : ""} ${chat.open ? "on" : ""}`}
      aria-label={bubbleLabel(chat.unread)}
      aria-expanded={chat.open}
      aria-controls="chat-panel"
      data-tip={variant === "rail" ? bubbleLabel(chat.unread) : undefined}
      data-chat-button={variant}
      style={variant === "float" ? { bottom: `calc(16px + env(safe-area-inset-bottom, 0px) + ${lift}px)` } : undefined}
      onClick={chat.toggle}
    >
      <Icon name="chat" width={24} height={24} />
      <span className="chat-fab-label">Chat</span>
      {badge && (
        <b className="chat-fab-badge" aria-hidden="true">
          {badge}
        </b>
      )}
    </button>
  );
}

/** Height of the visible sticky bottom bar of the page, so the phone bubble floats above it. */
function useActionBarLift(active: boolean): number {
  const [lift, setLift] = useState(0);
  useEffect(() => {
    if (!active) return;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let h = 0;
        for (const el of document.querySelectorAll<HTMLElement>(".action-bar, .analyte-order-bar")) {
          const r = el.getBoundingClientRect();
          if (r.height > 0 && r.bottom >= window.innerHeight - 2) h = Math.max(h, window.innerHeight - r.top);
        }
        setLift(Math.round(h));
      });
    };
    measure();
    const mo = new MutationObserver(measure);
    mo.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active]);
  return lift;
}

export function ChatPanel() {
  const chat = useChat();
  const panel = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const [confirmHide, setConfirmHide] = useState<string | null>(null);
  const count = chat.messages.length;

  useEffect(() => {
    if (chat.open) input.current?.focus();
  }, [chat.open]);
  // Esc closes even when focus fell out of the panel (the send button disables itself after a send),
  // unless a modal sheet is open on top: that one takes the key.
  useEffect(() => {
    if (!chat.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || document.querySelector("dialog[open]")) return;
      if (panel.current?.contains(document.activeElement)) return; // handled by the panel itself
      chat.setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chat.open, chat.setOpen]);
  // Newest at the bottom: follow new messages.
  useLayoutEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [count, chat.open]);

  if (!chat.enabled || !chat.open) return null;

  function close() {
    chat.setOpen(false);
    document.querySelector<HTMLButtonElement>(".chat-fab:not([hidden])")?.focus();
  }

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== "Tab" || !panel.current) return;
    const f = [...panel.current.querySelectorAll<HTMLElement>("button, textarea, [href], [tabindex]:not([tabindex='-1'])")].filter((x) => !x.hasAttribute("disabled"));
    if (!f.length) return;
    const first = f[0]!;
    const last = f[f.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div className="chat-panel" id="chat-panel" role="dialog" aria-modal="false" aria-labelledby="chat-title" ref={panel} onKeyDown={onKeyDown} data-testid="chat-panel">
      <div className="chat-head">
        <h2 className="h3" id="chat-title">
          Chat entre sucursales
        </h2>
        <button type="button" className="btn btn-ghost btn-icon" aria-label="Cerrar chat" title="Cerrar chat" onClick={close}>
          <Icon name="x" />
        </button>
      </div>
      <div className="chat-list" ref={list} aria-live="polite">
        {chat.loading && count === 0 && <p className="muted small chat-empty">Cargando…</p>}
        {!chat.loading && count === 0 && <p className="muted small chat-empty">Aún no hay mensajes. Escribe el primero.</p>}
        {chat.messages.map((m) => (
          <article key={m.id} className={`chat-msg ${m.mine ? "mine" : ""} ${m.hidden ? "hidden-msg" : ""}`} data-testid="chat-msg">
            <header className="chat-meta">
              <span className="chat-author">{m.mine ? "Tú" : m.author_name}</span>
              <span className="chat-branch">{m.branch_name}</span>
              <time dateTime={m.created_at}>{messageTime(m.created_at)}</time>
            </header>
            {m.hidden ? <p className="chat-body muted">Mensaje ocultado por su autor.</p> : <p className="chat-body">{m.body}</p>}
            {m.mine && !m.hidden && (
              <div className="chat-actions">
                {confirmHide === m.id ? (
                  <>
                    <span className="small">¿Ocultar este mensaje?</span>
                    <button type="button" className="link-btn" onClick={() => void chat.hide(m.id).then(() => setConfirmHide(null))}>
                      Ocultar
                    </button>
                    <button type="button" className="link-btn" onClick={() => setConfirmHide(null)}>
                      Cancelar
                    </button>
                  </>
                ) : (
                  <button type="button" className="link-btn" onClick={() => setConfirmHide(m.id)}>
                    Ocultar
                  </button>
                )}
              </div>
            )}
          </article>
        ))}
      </div>
      {chat.error && <p className="chat-error small">{chat.error}</p>}
      <form
        className="chat-compose"
        onSubmit={(e) => {
          e.preventDefault();
          void chat.send().then(() => input.current?.focus());
        }}
      >
        <textarea
          ref={input}
          aria-label="Mensaje para las sucursales"
          placeholder="Escribe un mensaje para las sucursales…"
          rows={2}
          maxLength={CHAT_MESSAGE_MAX}
          value={chat.draft}
          onChange={(e) => chat.setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void chat.send().then(() => input.current?.focus());
            }
          }}
        />
        <button type="submit" className="btn btn-primary btn-icon chat-send" disabled={!chat.draft.trim() || chat.sending} aria-label="Enviar mensaje">
          <Icon name="send" />
        </button>
      </form>
      <p className="chat-note">No compartas datos de pacientes aquí.</p>
    </div>
  );
}
